import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import { mainLayoutStyles, showNotification } from "../../../../../constants";
import {
  deleteUpload,
  isImageUpload,
  listUploads,
} from "../../hooks/SalesPagesHook";
import UploadFileModal from "../../components/UploadFileModal/UploadFileModal";

/**
 * GeneralAdmin > QuickTales.com > Uploads.
 * Displays the Storage `uploads/` folder as an image grid (placeholder icon for
 * non-images) with per-file Get URL + Delete actions and an Upload modal.
 */
const SalesUploadsContainer = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    loadUploads();
  }, []);

  const loadUploads = async () => {
    try {
      setLoading(true);
      const data = await listUploads();
      setItems(data);
    } catch (error) {
      console.log("Error loading uploads:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetUrl = async (item) => {
    try {
      if (navigator.clipboard && item.url) {
        await navigator.clipboard.writeText(item.url);
        showNotification("Public URL copied to clipboard");
      } else {
        window.prompt("Public URL", item.url);
      }
    } catch (error) {
      window.prompt("Public URL", item.url);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await deleteUpload(item.fullPath);
      showNotification("File deleted");
      await loadUploads();
    } catch (error) {
      console.log("Error deleting file:", error.message);
      showNotification("Error deleting file");
      setLoading(false);
    }
  };

  const handleUploaded = () => {
    setModalOpen(false);
    loadUploads();
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle="QuickTales.com — Uploads"
        bgColor="#fff"
        hideBackArrow
        buttonText="Upload"
        setEditEnabled={() => setModalOpen(true)}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid
          container
          direction="column"
          style={{ padding: 30, marginTop: 40 }}
        >
          {items.length === 0 && !loading ? (
            <Typography sx={emptyStyles}>
              No uploads yet. Click "Upload" to add a file.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {items.map((item) => {
                const image = isImageUpload(item);
                return (
                  <Grid item key={item.fullPath} xs={12} sm={6} md={4} lg={3}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <Box sx={thumbWrapStyles}>
                        {image && item.url ? (
                          <img
                            src={item.url}
                            alt={item.name}
                            style={thumbImgStyles}
                          />
                        ) : (
                          <InsertDriveFileOutlinedIcon
                            sx={{ fontSize: 64, color: "#B0BEC5" }}
                          />
                        )}
                      </Box>
                      <CardContent sx={{ p: 1.5 }}>
                        <Tooltip title={item.name}>
                          <Typography sx={nameStyles} noWrap>
                            {item.name}
                          </Typography>
                        </Tooltip>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            mt: 0.5,
                          }}
                        >
                          <Tooltip title="Copy public URL">
                            <IconButton
                              size="small"
                              onClick={() => handleGetUrl(item)}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(item)}
                              sx={{ color: "#B00020" }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
      </Grid>

      <UploadFileModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onUploaded={handleUploaded}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default SalesUploadsContainer;

const emptyStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  color: "#808080",
  p: 2,
};

const thumbWrapStyles = {
  height: 140,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#F5F5F5",
  overflow: "hidden",
};

const thumbImgStyles = {
  maxWidth: "100%",
  maxHeight: "100%",
  objectFit: "contain",
};

const nameStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 13,
  color: "#020303",
};
